<template>
  <RevCard
    ref="review-card"
    class="space-y-20 p-16 md:space-y-24"
    data-qa="user-comment"
    data-test="review-display"
  >
    <div
      class="flex w-full flex-col items-start justify-between gap-12"
      :class="{ 'md:flex-row': !forceMobileDisplay }"
    >
      <div class="w-full" :class="{ 'md:w-auto': !forceMobileDisplay }">
        <div
          class="flex items-center justify-between gap-8"
          :class="{ 'md:justify-start': !forceMobileDisplay }"
        >
          <h3 class="heading-3">{{ userName }}</h3>

          <span
            v-if="isVerifiedPurchaseEnabled"
            class="bg-static-info-mid text-static-info-hi rounded-xs body-2-bold flex max-w-full items-center truncate py-0 pl-2 pr-4"
          >
            <IconOfficialFilled class="mr-4" size="16" />

            {{ i18n(translations.verifiedPurchase) }}
          </span>

          <span
            v-else
            class="bg-static-info-mid text-static-info-hi rounded-xs body-2-bold flex max-w-full items-center truncate py-0 pl-2 pr-4"
          >
            <IconQuality class="mr-4" size="16" />

            {{ i18n(translations.verified) }}
          </span>
        </div>

        <p
          v-if="reviewInfotext"
          class="text-static-default-low body-2"
          data-allow-mismatch="text"
        >
          {{ reviewInfotext }}
          <Flag
            v-if="countryCode"
            aria-hidden="true"
            class="ml-2 inline-block align-baseline"
            :country-code="countryCode as MarketCountryCode"
          />
        </p>
      </div>

      <RevRating :score="averageRate" />
    </div>

    <!-- PhotosSlider (or any other component) can be injected here-->
    <slot />

    <div v-if="mainReviewComment" class="flex flex-col gap-8">
      <ReviewText
        :comment="mainReviewComment"
        data-test="main-review-comment"
        :force-expand="expandComment"
        :tags
        :tracking="trackingData.onClick"
      />

      <!-- Translation -->
      <div v-if="isTranslatable" class="flex flex-wrap items-center gap-4">
        <p v-if="isTranslated" class="text-static-default-low caption">
          {{ translatedFrom }}
        </p>

        <RevDivider v-if="isTranslated" class="h-16" variant="vertical" />

        <RevLink
          class="text-static-default-low leading-caption text-caption-bold"
          data-test="toggle-translation-button"
          @click="onToggleTranslation"
        >
          {{ toggleTranslationLabel }}
        </RevLink>
      </div>
    </div>
    <div class="flex flex-col items-end gap-8 md:flex-row md:items-center">
      <ProductDetails
        :backboxGrade="salesOrderItem?.backboxGrade"
        class="w-full flex-1 md:w-auto"
        :display-product-link
        :display-seller
        :force-mobile-display
        :is-translatable
        :product
        :seller
        :tracking="trackingData.onClick"
      />

      <ReviewVoteButton
        v-if="FF_ENABLE_REVIEW_VOTING"
        :review-id="id"
        :tracking="trackingData.onClick"
        :votes-count="voteCount"
      />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import { RevRating } from '@ds/components/Rating'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'
import { IconQuality } from '@ds/icons/IconQuality'

import {
  useReviewImpressionTracker,
  useTrackReviewImpressionInViewport,
} from '../../composables/useTrackReviewImpression'
import { useTranslateComment } from '../../composables/useTranslateComment'
import type { UserComment } from '../../models/UserComment.type'

import ProductDetails from './ProductDetails/ProductDetails.vue'
import translations from './ReviewDisplay.translations'
import ReviewText from './ReviewText/ReviewText.vue'
import ReviewVoteButton from './ReviewVoteButton/ReviewVoteButton.vue'

export type UserCommentProps = UserComment & {
  trackingData?: {
    onClick: Record<string, unknown>
    onImpression: Record<string, unknown>
  }
  trackReviewImpression?: boolean
  displaySeller?: boolean
  forceMobileDisplay?: boolean
  displayProductLink?: boolean
  voteCount?: number
}

const i18n = useI18n()
const { trackClick } = useTracking()
const expandComment = ref(false)
const reviewRef = useTemplateRef<HTMLElement>('review-card')
const experiments = useExperiments()

const { FF_ENABLE_REVIEW_VOTING } = useRuntimeConfig().public

const { trackExtendedReviewImpression } = useReviewImpressionTracker()

const isVerifiedPurchaseEnabled =
  experiments['experiment.reviewsVerifiedPurchase'] === 'withVerifiedPurchase'

const props = withDefaults(defineProps<UserCommentProps>(), {
  trackingData: () => ({
    onClick: {},
    onImpression: {},
  }),
  translatedComment: undefined,
  trackReviewImpression: false,
  seller: undefined,
  displaySeller: false,
  forceMobileDisplay: false,
  displayProductLink: true,
  voteCount: 0,
  tags: undefined,
})

const {
  mainReviewComment,
  isTranslated,
  toggleTranslation,
  isTranslatable,
  toggleTranslationLabel,
  translatedFrom,
} = useTranslateComment(props)

watch(
  () => props.translatedComment,
  (newTranslatedComment) => {
    if (newTranslatedComment) {
      expandComment.value = true
    }
  },
)

const userName = computed(() => {
  if (props.customer.firstName && props.customer.lastName) {
    return `${props.customer.firstName} ${props.customer.lastName}`
  }

  return (
    props.customer.firstName ??
    props.customer.lastName ??
    i18n(translations.anonymousUser)
  )
})

const reviewInfotext = computed(() =>
  props.reviewDate && props.countryCode
    ? i18n(translations.reviewInfo, {
        reviewDate: i18n.date(new Date(props.reviewDate), {
          dateStyle: 'short',
        }),
        country: i18n.country(props.countryCode),
      })
    : '',
)

async function onToggleTranslation() {
  trackClick({
    name: isTranslated.value ? 'see_original' : 'translate',
    ...props.trackingData?.onClick,
  })

  await toggleTranslation()
}

onMounted(() => {
  if (props.trackReviewImpression) {
    useTrackReviewImpressionInViewport(reviewRef, {
      userComment: props,
      isTranslated,
    })
  }
})

watch(
  () => props.id,
  () => {
    if (props.trackReviewImpression) {
      trackExtendedReviewImpression({
        userComment: props,
        isTranslated: isTranslated.value,
      })
    }
  },
)
</script>
